import { useCallback, useMemo } from "react";
import { useAppContext } from "@/context/context";
import { customTextAggFunc } from "@/utils/helper_functions";
import {
  ColDef,
  GetContextMenuItemsParams,
  MenuItemDef,
  SideBarDef,
} from "ag-grid-community";
import {
  IAccountMetadata,
  IPortfolioMetadata,
} from "@pulse-ui/codegen/generated_ts/org_metadata";
import { IAGAlertManagerDataRow } from "@pulse-ui/types/components/AGTable";
import { parseDashedFieldNames } from "../utils/transformers";

const ACCOUNT = "account";
const PORTFOLIO = "portfolio";

interface IProps {
  onTestFire: (checkName: string) => void;
  onEdit: (row: IAGAlertManagerDataRow, type: string) => void;
  onDuplicate: (row: IAGAlertManagerDataRow, type: string) => void;
  onStop: (alertName: string) => void;
}

export const useAlertManagerTable = ({
  onTestFire,
  onEdit,
  onDuplicate,
  onStop,
}: IProps) => {
  const { context_state } = useAppContext();

  const entityDisplayNameGetter = useCallback(
    (params: any, type: typeof ACCOUNT | typeof PORTFOLIO): string => {
      if (
        params?.data &&
        type &&
        context_state?.org_metadata?.portfolios?.length > 0
      ) {
        const { portfolios } = context_state.org_metadata;

        if (type === PORTFOLIO && params.data.portfolio) {
          const portfolio = portfolios.find(
            (p: IPortfolioMetadata) =>
              p.portfolio_id === params.data.portfolio
          );
          return portfolio ? portfolio.display_name : "-";
        }

        if (type === ACCOUNT && params.data.account) {
          for (const portfolio of portfolios) {
            const account = portfolio.accounts?.find(
              (a: IAccountMetadata) => a.account_id === params.data.account
            );
            if (account) return account.display_name;
          }
        }
      }
      return "-";
    },
    [context_state]
  );

  const colDefs = useMemo<ColDef<IAGAlertManagerDataRow>[]>(
    () => [
      {
        headerName: "Name",
        field: "name",
        aggFunc: customTextAggFunc,
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Portfolio",
        field: "portfolio",
        valueGetter: (params: any) =>
          entityDisplayNameGetter(params, PORTFOLIO),
        aggFunc: customTextAggFunc,
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Account",
        field: "account",
        valueGetter: (params: any) =>
          entityDisplayNameGetter(params, ACCOUNT),
        aggFunc: customTextAggFunc,
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Type",
        field: "alert_type",
        valueFormatter: (params: any) => parseDashedFieldNames(params.value),
        cellClass: "ag-center-aligned-cell",
        aggFunc: customTextAggFunc,
        rowGroup: true,
        hide: true,
      },
      {
        headerName: "Destinations",
        field: "alert_destinations",
        aggFunc: customTextAggFunc,
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Parameters",
        field: "parameters",
        aggFunc: customTextAggFunc,
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Display Value",
        field: "display_value",
        aggFunc: customTextAggFunc,
        cellClass: "ag-center-aligned-cell",
      },
    ],
    [entityDisplayNameGetter]
  );

  const defaultColDef: ColDef = {
    filter: "agTextColumnFilter",
  };

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: "Group",
      minWidth: 280,
      filter: "agTextColumnFilter",
      cellRendererParams: { suppressCount: false },
    }),
    []
  );

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
      ],
      defaultToolPanel: "columns",
    }),
    []
  );

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): MenuItemDef[] => {
      const isGroupRow = params?.node?.group || false;
      if (isGroupRow) return [];

      return [
        {
          name: "Test Fire Alert",
          action: () => onTestFire(params?.node?.data?.name),
          cssClasses: ["pulse-color--background-100"],
        },
        {
          name: "Edit Alert",
          action: () =>
            onEdit(
              params?.node?.data,
              params?.node?.data?.original_row?.config_name
            ),
          cssClasses: ["pulse-color--background-100"],
        },
        {
          name: "Duplicate Alert",
          action: () =>
            onDuplicate(
              params?.node?.data,
              params?.node?.data?.original_row?.config_name
            ),
          cssClasses: ["pulse-color--background-100"],
        },
        {
          name: "Delete Alert",
          action: () => onStop(params?.node?.data?.name),
          cssClasses: ["pulse-color--error-400"],
        },
      ];
    },
    [onTestFire, onEdit, onDuplicate, onStop]
  );

  return { colDefs, defaultColDef, autoGroupColumnDef, sideBar, getContextMenuItems };
};
